import { supabase } from '@shared/lib/supabase';

/**
 * Checks if the profiles table exists
 * 
 * @returns Promise that resolves to a boolean indicating if the profiles table exists
 */
export const checkIfProfilesTableExists = async (): Promise<boolean> => {
  try {
    // Try to count profiles
    const { count, error } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      // Check if the error is specifically about the table not existing
      if (error.message.includes('relation "profiles" does not exist') || 
          error.message.includes('profiles table does not exist')) {
        console.log('Profiles table does not exist');
        return false;
      }
      
      console.error('Error checking if profiles table exists:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Exception checking if profiles table exists:', error);
    return false;
  }
};

/**
 * Creates a user profile in the database using direct table insertion
 * This is a more reliable approach than depending on triggers
 * 
 * @param userId The user's UUID from Supabase Auth
 * @param fullName The user's full name (optional)
 * @returns Object containing success status and any error message
 */
export const createProfileDirectly = async (
  userId: string, 
  fullName: string = ''
): Promise<{ success: boolean; error?: string }> => {
  try {
    // First check if profiles table exists
    const tableExists = await checkIfProfilesTableExists();
    if (!tableExists) {
      console.error('Cannot create profile: profiles table does not exist');
      return { 
        success: false, 
        error: 'The profiles table does not exist in the database. Please contact support.'
      };
    }
    
    console.log(`Directly creating profile for user: ${userId}`);
    
    // First check if profile already exists
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();
      
    if (checkError) {
      console.error('Error checking for existing profile:', checkError);
      return { success: false, error: checkError.message };
    }
    
    // If profile already exists, return success
    if (existingProfile) {
      console.log(`Profile already exists for user: ${userId}`);
      return { success: true };
    }
    
    // Create new profile with direct SQL
    const { error: insertError } = await supabase.rpc(
      'execute_sql',
      { 
        sql_statement: `
          INSERT INTO public.profiles (id, full_name, created_at, updated_at)
          VALUES ('${userId}', '${fullName.replace(/'/g, "''")}', now(), now())
        `
      }
    );
    
    if (insertError) {
      console.error('Error creating profile with SQL:', insertError);
      
      // Try direct insert as fallback
      const { error: directError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          full_name: fullName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (directError) {
        console.error('Error with direct insert:', directError);
        return { success: false, error: directError.message };
      }
    }
    
    console.log(`Profile created successfully for user: ${userId}`);
    return { success: true };
  } catch (error) {
    console.error('Unexpected error creating profile directly:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error creating profile'
    };
  }
};

/**
 * Creates a user profile in the database
 * This function is called after successful signup to ensure a profile exists
 * 
 * @param userId The user's UUID from Supabase Auth
 * @param fullName The user's full name (optional)
 * @returns Object containing success status and any error message
 */
export const createUserProfile = async (
  userId: string, 
  fullName: string = ''
): Promise<{ success: boolean; error?: string; method?: string; details?: any }> => {
  try {
    // First check if profiles table exists
    console.log(`Creating profile for user: ${userId}`);
    
    // Try edge function approach first
    try {
      const { data: repairData, error: repairError } = await supabase.functions.invoke(
        'repair-user-profile',
        {
          body: { 
            userId, 
            email: null, // We don't need email when we have userId
            fullName 
          }
        }
      );
      
      if (repairError) {
        throw repairError;
      }
      
      console.log(`Profile creation via edge function succeeded for user: ${userId}`);
      return { success: true, method: 'edge_function', details: repairData };
    } catch (edgeFunctionError) {
      console.warn('Edge function profile creation failed:', edgeFunctionError);
      // Continue to next approach
      }
    
    // Try RPC function approach
    try {
      const { data, error } = await supabase.rpc(
        'create_user_profile',
        { 
          user_id: userId,
          full_name: fullName
        }
      );
      
      if (error) {
        throw error;
      }
      
      console.log(`Profile created via RPC for user: ${userId}`);
      return { success: true, method: 'rpc' };
    } catch (rpcError) {
      console.warn('RPC profile creation failed:', rpcError);
      // Continue to next approach
    }
    
    // Try direct insert as a last resort
    try {
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
        
      if (checkError && !checkError.message.includes('No rows found')) {
        console.error('Error checking for existing profile:', checkError);
        throw checkError;
      }
      
      if (existingProfile) {
        console.log(`Profile already exists for user: ${userId}`);
        return { success: true, method: 'exists' };
      }

      // Create profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          full_name: fullName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (insertError) {
        throw insertError;
      }
      
      console.log(`Profile created via direct insert for user: ${userId}`);
      return { success: true, method: 'direct' };
    } catch (directError) {
      console.error('Direct profile creation failed:', directError);
      return { 
        success: false, 
        error: directError instanceof Error ? directError.message : 'Unknown error creating profile',
        method: 'direct_failed' 
      };
    }
  } catch (error) {
    console.error('Unexpected error creating profile:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error creating profile',
      method: 'all_methods_failed'
    };
  }
};
